import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import {
    Button,
    Typography,
    Card,
    Checkbox,
    Input,
} from "@material-tailwind/react";

import { DialogAddPart } from "./DialogAddParts";
import { Search, Refresh } from "iconoir-react";
import { getMoneyFormat } from "../../../../utils/formatViews";
import { Part } from "../../../../redux/vehicles/actions";
import {
    listPartsSuccess,
    setPartRequest,
    updatePartsRequest,
} from "../../../../redux/parts/actions";
import AppImages from "../../../../assets/AppImages";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import Loader from "../../../../components/Loader";

const TABLE_HEAD = [
    { n: "Part Name", className: "w-48" },
    { n: "YMMT", className: "" },
    { n: "Part Number", className: "" },
    { n: "Quantity", className: "" },
    // { n: "Status", className: "" },
    { n: "Total Cost", className: "w-28" },
    { n: "Location", className: "" },
];

interface DashboardPartsInventoryTableProps {
    title: string;
    description?: string;
    parts: Part[];
    save_as: string;
    handleRefresh: () => void;
}

export const DashboardPartsInventoryTable: React.FC<DashboardPartsInventoryTableProps> = ({
    title,
    description,
    parts,
    save_as,
    handleRefresh
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading } = useSelector((state: RootState) => state.parts);
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState<string>("");
    const [filterParts, setFilterParts] = React.useState<Part[]>([]);
    const [selectedParts, setSelectedParts] = React.useState<number[]>([]);

    React.useEffect(() => {
        const handleFilterApply = () => {
            let result = [...parts];

            if (search) {
                result = result.filter((el) => {
                    const searchString = `${el?.name} ${el?.number} ${el?.qty} ${el?.status} ${el?.cost} ${el?.delivery_to}`;
                    return searchString.toLowerCase().includes(search.toLowerCase());
                });
            }

            setFilterParts(result);
        };

        handleFilterApply();
    }, [parts, search]);

    const handleCheckboxChange = (id: number) => {
        setSelectedParts((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    return (
        <React.Fragment>
            <Card>
                <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                    <div>
                        <Typography type="lead" color="default" className="font-semibold mb-1">
                            {title}
                        </Typography>
                        {description && (
                            <Typography type="small" className="text-foreground block">
                                {description}
                            </Typography>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                        <Input
                            placeholder="Search here..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full sm:w-72"
                        >
                            <Input.Icon>
                                <Search className="w-full h-full" />
                            </Input.Icon>
                        </Input>
                        <div className="flex gap-3 sm:w-max w-full">
                            {/*
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-2 sm:w-auto w-full"
                            >
                                Export
                                <Download className="w-4 h-4" />
                            </Button>
                            */}
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    handleRefresh();
                                }}
                            >
                                <Refresh className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="error"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    if (selectedParts.length == 0) {
                                        return;
                                    }

                                    withReactContent(Swal)
                                        .fire({
                                            title: "Are you sure?",
                                            text: `You are about to remove ${selectedParts.length} parts`,
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonColor: "#EF4444",
                                            confirmButtonText: "Remove",
                                        })
                                        .then((result: any) => {
                                            if (result.isConfirmed) {
                                                dispatch(
                                                    updatePartsRequest({
                                                        parts: selectedParts.map((id) => ({
                                                            id: id,
                                                            is_removed: true,
                                                        })),
                                                        update_fields: ["id", "is_removed"],
                                                        save_as: save_as,
                                                    })
                                                );
                                                dispatch(
                                                    listPartsSuccess({
                                                        data: parts.filter(
                                                            (t) =>
                                                                !selectedParts.includes(t.id || 0)
                                                        ),
                                                        save_as: save_as,
                                                    })
                                                );
                                                setSelectedParts([]);
                                            }
                                        })
                                        .catch((error: any) => {
                                            console.log("error", error);
                                        });
                                }}
                            >
                                Remove
                                <AppImages.iconTrash className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                color="primary"
                                className="flex items-center gap-2 sm:w-auto w-full text-nowrap"
                                onClick={() => setIsOpen(true)}
                            >
                                Add Part
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-240px)] overflow-y-auto scrollbar-hide">
                    <table className="w-full min-w-max table-auto">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((el, idx: number) => (
                                    <th
                                        key={idx}
                                        className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                    >
                                        {idx == 0 ? (
                                            <div className="flex items-center gap-5">
                                                <Checkbox
                                                    id="checkbox_all"
                                                    key={`${Math.random()}`}
                                                    checked={
                                                        selectedParts.length == filterParts.length
                                                    }
                                                    onChange={() => {
                                                        if (
                                                            selectedParts.length ==
                                                            filterParts.length
                                                        ) {
                                                            setSelectedParts([]);
                                                        } else {
                                                            setSelectedParts(
                                                                filterParts.map((el) => el.id || 0)
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <Checkbox.Indicator />
                                                </Checkbox>
                                                <Typography
                                                    type="small"
                                                    color="default"
                                                    className="block font-medium"
                                                >
                                                    {el.n}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {el.n}
                                            </Typography>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filterParts?.map((el: Part) => (
                                <tr
                                    key={el.id}
                                    onClick={(e) => {
                                        if (
                                            (e.target as HTMLElement).tagName === "INPUT" ||
                                            (e.target as HTMLElement).tagName === "IMG" ||
                                            (e.target as HTMLElement).tagName === "LABEL"
                                        ) {
                                            return;
                                        }

                                        dispatch(setPartRequest(el));
                                        navigate("/dashboard/parts/inventory/" + el.id);
                                    }}
                                >
                                    <td className="px-4 border-b border-surface">
                                        <div className="flex items-center gap-5">
                                            <Checkbox
                                                key={`${el.id}_${Math.random()}`}
                                                id={`checkbox_${el?.id || 0}`}
                                                checked={selectedParts.includes(el?.id || 0)}
                                                onChange={() => handleCheckboxChange(el?.id || 0)}
                                            >
                                                <Checkbox.Indicator />
                                            </Checkbox>
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="py-4 overflow-hidden flex items-center text-ellipsis w-48 !text-nowrap text-foreground font-medium"
                                            >
                                                {el.name}
                                            </Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el?.vehicle?.ymmt || "N/A"}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.number}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.qty}
                                        </Typography>
                                    </td>
                                    {/* <td className="p-4 border-b border-surface">
                                        {el.status && (
                                            <Chip
                                                size="sm"
                                                isPill={false}
                                                className="w-max"
                                                color={getPartStatusColor(el.status)}
                                            >
                                                <Chip.Label className="capitalize">
                                                    {el.display_status}
                                                </Chip.Label>
                                            </Chip>
                                        )}
                                    </td> */}
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {getMoneyFormat(el?.cost)}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {(el?.store?.title || "N/A") + " " + (el?.store?.location || "")}
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card.Body>
            </Card>

            {loading && (
                <div className="flex justify-center items-center h-full">
                    <Loader />
                </div>
            )}

            {isOpen && (
                <DialogAddPart
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    onSubmit={(part: Part) => {
                        setIsOpen(false);
                        dispatch(
                            updatePartsRequest({
                                parts: [part],
                                update_fields: Object.keys(part),
                                save_as: save_as,
                            })
                        );
                    }}
                />
            )}
        </React.Fragment>
    );
};
